import type { ReactNode } from 'react';
import { useLocale, useT } from '../i18n/locale-context';
import { formatBudgetCompact, withCurrency } from '../lib/format';

/**
 * Soliq (tax registry) trust facts for the buyer org — «О заказчике» /
 * «Buyurtmachi haqida». Rendered as «field → value» spec rows (matching the
 * procedure panel on the same page), one per fact that resolved: registry
 * status, VAT-payer, уставный фонд, «работает с YYYY», and — as a warning
 * banner — a liquidation year.
 *
 * Status is localized from `statusGroup` (ACTIVE / TAX_FREE / INACTIVE /
 * LIQUIDATED), NOT the raw `status` string — the raw string is only ever
 * Russian, so it read wrong on the Uzbek page. ACTIVE is the positive
 * good-standing signal (teal + check); INACTIVE/LIQUIDATED are amber warnings;
 * TAX_FREE is neutral. When `statusGroup` is unknown we fall back to the raw
 * `status` string (neutral), so a new group never renders blank.
 *
 * Two call sites: tender-detail spreads `item.buyerTrust` (has `regDate`) and
 * passes `profileLink` (rendered inside the card footer); the public buyer/
 * supplier profile has no `regDate` and passes no `profileLink`. Renders
 * nothing when no fact resolved AND no `profileLink` was passed; renders the
 * link alone when a link is passed but no fact resolved.
 *
 * ── The DXMAP rating rows ───────────────────────────────────────────────────
 *
 * `dxmapRateType` is the STATE's own rating letter for the company — the
 * «barqarorlik reytingi» (business stability rating) computed from Tax
 * Committee data (presidential decree of 2024-01-23, 23 criteria), which the
 * Ministry of Economy and Finance's portal (xarid.icppa.uz) republishes and we
 * read from there. A client pointed this out on 2026-10-07; the label used to
 * say «Рейтинг DXMAP», which named the carrier, not the author. Twelve bands,
 * AAA/AA/A · BBB/BB/B · CCC/CC/C · DDD/DD/D. It renders as a plain fact with a
 * source line, and three things about it are deliberate:
 *
 * 1. **Letter only, never the score.** DXMAP stores a 0–100 number alongside
 *    it but publishes no band thresholds, so «89» cannot be read as anything.
 *    The API does not even send it.
 * 2. **No tone.** Every other row here is `good`/`warn` because WE know what
 *    the fact means. We do not know what DXMAP's rating measures, so colouring
 *    a `D` red would be our judgement dressed as theirs. Neutral, always.
 * 3. **Attribution is not optional.** DXMAP's licence requires a credit on any
 *    surface reusing its data, so the source line renders whenever the rating
 *    does — it is part of the row, not decoration.
 */

type Tone = 'good' | 'neutral' | 'warn';

/** The КГД codes we have copy for (`kz-kgd-trust.ts`). */
const KGD_FLAGS = new Set([
  'DEBTOR_BANKRUPT',
  'DEBTOR_REHABILITATION',
  'DEBTOR_LIQUIDATION_NO_BANKRUPTCY',
  'BANKRUPT',
  'PSEUDO_ENTERPRISE',
  'REGISTRATION_INVALID',
  'REREGISTRATION_INVALID',
]);

/** Soliq status groups → row tone. `ACTIVE` is the good-standing signal. */
const STATUS_TONE: Record<string, Tone> = {
  ACTIVE: 'good',
  TAX_FREE: 'neutral',
  INACTIVE: 'warn',
  LIQUIDATED: 'warn',
};

function CheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mt-0.5 shrink-0"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mt-0.5 shrink-0"
      aria-hidden="true"
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

export function BuyerTrustStrip({
  status,
  statusGroup,
  vatPayer,
  charterFundUzs,
  regDate,
  liquidationDate,
  dxmapRateType,
  dxmapSector,
  kgdFlags,
  kind = 'buyer',
  profileLink,
}: {
  status?: string | null;
  statusGroup?: string | null;
  vatPayer?: boolean | null;
  charterFundUzs?: string | null;
  regDate?: string | null;
  liquidationDate?: string | null;
  /** DXMAP's rating LETTER (`AAA`…`D`). Absent/null → no rating row and no
   *  source line. Never the numeric score — see the component doc. */
  dxmapRateType?: string | null;
  /** DXMAP's «Faoliyat turi» for that rating, Uzbek Cyrillic. Rendered
   *  verbatim under a localized label; only shown alongside a letter. */
  dxmapSector?: string | null;
  /** KZ Tax Committee (КГД) register hits: DEBTOR_BANKRUPT / DEBTOR_REHABILITATION /
   *  DEBTOR_LIQUIDATION_NO_BANKRUPTCY (court-final insolvency) and BANKRUPT /
   *  PSEUDO_ENTERPRISE / REGISTRATION_INVALID / REREGISTRATION_INVALID (unreliable
   *  taxpayers). Each renders as a warning with a «по данным КГД» source line — these are
   *  the state's findings, not our score. Unknown codes are skipped, never shown raw. */
  kgdFlags?: string[] | null;
  /** Heading only: «О заказчике» (buyer, default) vs «О поставщике» (supplier). */
  kind?: 'buyer' | 'supplier';
  /** The buyer→profile link, rendered inside the card footer (tender detail only). */
  profileLink?: ReactNode;
}) {
  const t = useT();
  const { locale } = useLocale();

  // Budget-funded state institutions (ДАВЛАТ МУАССАСАСИ) report a NOMINAL 1 so'm
  // charter fund from soliq — accurate but meaningless. Real funds run to
  // millions+, so hide anything trivially small rather than show «1 сум».
  const fundUzs = charterFundUzs != null ? Number(charterFundUzs) : NaN;
  const fund =
    Number.isFinite(fundUzs) && fundUzs >= 1000
      ? formatBudgetCompact(charterFundUzs, locale)
      : null;
  const sinceYear = regDate ? regDate.slice(0, 4) : null;
  const liquidatedYear = liquidationDate ? liquidationDate.slice(0, 4) : null;

  // Localize the status from its group; fall back to the raw registry string.
  const statusLabel =
    statusGroup && STATUS_TONE[statusGroup] !== undefined
      ? t(`tender.trust.status.${statusGroup}`)
      : (status ?? null);
  const statusTone: Tone = statusGroup ? (STATUS_TONE[statusGroup] ?? 'neutral') : 'neutral';

  type Row = { key: string; label: string; value: string; tone: Tone };
  const rows: Row[] = [];
  if (statusLabel)
    rows.push({
      key: 'status',
      label: t('tender.trust.statusLabel'),
      value: statusLabel,
      tone: statusTone,
    });
  if (vatPayer === true)
    rows.push({
      key: 'vat',
      label: t('tender.trust.vatLabel'),
      value: t('tender.trust.vatYes'),
      tone: 'neutral',
    });
  if (fund)
    rows.push({
      key: 'fund',
      label: t('tender.trust.fund'),
      value: withCurrency(fund, t('tender.budget.unit')),
      tone: 'neutral',
    });
  if (sinceYear)
    rows.push({
      key: 'since',
      label: t('tender.trust.sinceLabel'),
      value: sinceYear,
      tone: 'neutral',
    });

  // The state rating, last: it is somebody else's fact, and it carries its own
  // source line below the list. Trimmed because DXMAP's sector labels arrive
  // with trailing spaces on a measurable share of rows.
  const rating = dxmapRateType?.trim() || null;
  const ratingSector = dxmapSector?.trim() || null;
  if (rating)
    rows.push({
      key: 'dxmapRating',
      label: t('tender.trust.dxmapRatingLabel'),
      value: rating,
      tone: 'neutral',
    });
  if (rating && ratingSector)
    rows.push({
      key: 'dxmapSector',
      label: t('tender.trust.dxmapSectorLabel'),
      value: ratingSector,
      tone: 'neutral',
    });

  const kgd = (kgdFlags ?? []).filter((f) => KGD_FLAGS.has(f));
  const hasContent = rows.length > 0 || liquidatedYear || kgd.length > 0;
  if (!hasContent) {
    return profileLink ? <div className="mt-2">{profileLink}</div> : null;
  }

  const valueClass: Record<Tone, string> = {
    good: 'text-brand',
    neutral: 'text-ink',
    warn: 'text-status-closing-text',
  };

  return (
    <section className="rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
      <h2 className="mb-3 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
        {t(kind === 'supplier' ? 'tender.trust.titleSupplier' : 'tender.trust.title')}
      </h2>

      <dl className="divide-y divide-edge-light">
        {rows.map((r) => (
          <div key={r.key} className="flex gap-3.5 py-2.5 first:pt-0">
            <dt className="w-[112px] shrink-0 pt-px text-[13px] font-semibold text-ink-muted">
              {r.label}
            </dt>
            <dd
              className={`flex items-start gap-1.5 text-[13.5px] font-semibold [overflow-wrap:anywhere] ${valueClass[r.tone]}`}
            >
              {r.tone === 'good' && <CheckIcon />}
              {r.tone === 'warn' && <WarnIcon />}
              <span>{r.value}</span>
            </dd>
          </div>
        ))}
      </dl>

      {rating && (
        <p className="mt-2.5 text-[12px] leading-snug text-ink-muted">
          {t('tender.trust.dxmapSource')}{' '}
          <a
            href="https://xarid.icppa.uz"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="underline underline-offset-2 hover:text-ink"
          >
            xarid.icppa.uz
          </a>
        </p>
      )}

      {liquidatedYear && (
        <div className="mt-3 flex items-center gap-1.5 rounded-md border border-status-closing-text/25 bg-status-closing-bg px-3 py-2 text-[13px] font-semibold text-status-closing-text">
          <WarnIcon />
          <span>{`${t('tender.trust.liquidated')} ${liquidatedYear}`}</span>
        </div>
      )}

      {kgd.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {kgd.map((f) => (
            <div
              key={f}
              className="flex items-center gap-1.5 rounded-md border border-status-closing-text/25 bg-status-closing-bg px-3 py-2 text-[13px] font-semibold text-status-closing-text"
            >
              <WarnIcon />
              <span>{t(`tender.trust.kgd.${f}`)}</span>
            </div>
          ))}
          <p className="text-[11px] text-ink-muted">{t('tender.trust.kgdSource')}</p>
        </div>
      )}

      {profileLink && <div className="mt-3 border-t border-edge-light pt-3">{profileLink}</div>}
    </section>
  );
}
